import { useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/layouts/AdminLayout";
import { useApi } from "@/hooks/useApi";
import RevenueChart from "./components/RevenueChart";
import TicketResolutionChart from "./components/TicketResolutionChart";
import ProjectCompletionMetrics from "./components/ProjectCompletionMetrics";
import KpiCards from "./components/KpiCards";
import LeadsFunnelChart from "./components/LeadsFunnelChart";
import api from "@/services/api";
import { useNotifications } from "@/context/NotificationContext";

interface StatCardProps {
  icon: string;
  label: string;
  value: string;
  growth?: number;
  color: string;
  bgColor: string;
  borderColor: string;
  sub?: string;
}

function EmailNotificationTester() {
  const { showToast } = useNotifications();
  const [recipientType, setRecipientType] = useState<"client" | "team">("client");
  const [selectedId, setSelectedId] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const { data: clients } = useApi<any[]>("/clients");
  const { data: teamMembers } = useApi<any[]>("/users/team");

  const recipients = recipientType === "client" 
    ? (clients || []).map(c => ({ id: c.id, label: c.company_name })) 
    : (teamMembers || []).map(t => ({ id: t.id, label: t.full_name }));

  const handleSend = async () => {
    if (!selectedId || !subject || !message) {
      showToast("error", "Please fill all fields");
      return;
    }

    setSending(true);
    try {
      await api.post("/admin/send-email", {
        toId: selectedId,
        toType: recipientType,
        subject,
        message,
      });
      showToast("success", "Email sent successfully!");
      setSubject("");
      setMessage("");
    } catch (err: any) {
      showToast("error", err.response?.data?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-silver text-xs font-medium mb-1.5 uppercase tracking-wider">Recipient Type</label>
          <select 
            value={recipientType} 
            onChange={(e) => { setRecipientType(e.target.value as any); setSelectedId(""); }}
            className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50"
          >
            <option value="client">Client</option>
            <option value="team">Team Member</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-silver text-xs font-medium mb-1.5 uppercase tracking-wider">Select Recipient</label>
          <select 
            value={selectedId} 
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50"
          >
            <option value="">Select {recipientType}</option>
            {recipients.map(r => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-silver text-xs font-medium mb-1.5 uppercase tracking-wider">Subject</label>
        <input 
          type="text" 
          value={subject} 
          onChange={(e) => setSubject(e.target.value)}
          className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50"
          placeholder="Test Email Subject"
        />
      </div>

      <div>
        <label className="block text-silver text-xs font-medium mb-1.5 uppercase tracking-wider">Message</label>
        <textarea 
          value={message} 
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 resize-none"
          placeholder="Write your test message here..."
        />
      </div>

      <button 
        onClick={handleSend}
        disabled={sending || !selectedId || !subject || !message}
        className="w-full bg-electric-blue text-deep-black font-semibold py-2.5 rounded-xl hover:shadow-glow-blue transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {sending ? "Sending..." : "Send Test Email"}
      </button>
    </div>
  );
}

function StatCard({ icon, label, value, growth, color, bgColor, borderColor, sub }: StatCardProps) {
  return (
    <div className={`bg-dark-card border ${borderColor} rounded-2xl p-5 hover:border-opacity-60 transition-all duration-300 group`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${bgColor} border ${borderColor} ${color}`}>
          <i className={`${icon} text-lg`} />
        </div>
        {growth !== undefined && (
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${growth >= 0 ? "text-cyber-green bg-cyber-green/10 border border-cyber-green/20" : "text-cyber-red bg-cyber-red/10 border border-cyber-red/20"}`}>
            {growth >= 0 ? "+" : ""}{growth}%
          </span>
        )}
      </div>
      <div className="text-2xl font-black text-white mb-1 group-hover:text-electric-blue transition-colors" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {value}
      </div>
      <div className="text-silver text-sm">{label}</div>
      {sub && <div className="text-silver-dim text-xs mt-1">{sub}</div>}
    </div>
  );
}

function minutesAgoLabel(iso: string | undefined | null) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.max(0, Math.floor(diffMin / 60));
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.max(0, Math.floor(diffHr / 24));
  return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
}

function buildActivityFeed({ leads, projects, tickets }: { leads: Lead[]; projects: Project[]; tickets: Ticket[] }) {
  const leadItems = leads
    .map((l) => ({
      id: `lead_${l.id}`,
      icon: "ri-user-add-line",
      color: "text-electric-blue bg-electric-blue/10 border-electric-blue/20",
      text: `New lead — ${l.name}`,
      time: minutesAgoLabel((l as any).created_at),
      sortTs: new Date((l as any).created_at || 0).getTime() || 0,
    }))
    .filter((x) => x.time);

  const projectItems = projects
    .map((p) => ({
      id: `proj_${p.id}`,
      icon: "ri-folder-line",
      color: "text-cyber-green bg-cyber-green/10 border-cyber-green/20",
      text: `Project updated — ${p.title}`,
      time: minutesAgoLabel((p as any).updated_at || (p as any).created_at),
      sortTs: new Date((p as any).updated_at || (p as any).created_at || 0).getTime() || 0,
    }))
    .filter((x) => x.time);

  const ticketItems = tickets
    .map((t) => ({
      id: `tick_${t.id}`,
      icon: "ri-customer-service-line",
      color: "text-cyber-red bg-cyber-red/10 border-cyber-red/20",
      text: `Ticket — ${t.subject}`,
      time: minutesAgoLabel((t as any).created_at),
      sortTs: new Date((t as any).created_at || 0).getTime() || 0,
    }))
    .filter((x) => x.time);

  return [...leadItems, ...projectItems, ...ticketItems]
    .sort((a, b) => b.sortTs - a.sortTs)
    .slice(0, 6)
    .map(({ id, icon, color, text, time }) => ({ id, icon, color, text, time }));
}


interface AdminStats {
  totalClients: number;
  activeProjects: number;
  totalRevenue: number;
  mrr: number;
  newLeads: number;
  openTickets: number;
  urgentTickets: number;
  revenueByMonth: { month: string; revenue: number }[];
}

interface Lead {
  id: string;
  name: string;
  email: string;
  company: string | null;
  service_interest: string | null;
  status: string;
  created_at: string;
}

interface Project {
  id: string;
  title: string;
  status: string;
  progress: number;
  client_id: string;
}

import { Ticket } from "@/types";

export default function AdminOverview() {
  const [activeTab, setActiveTab] = useState<"leads" | "projects" | "tickets">("leads");
  const [analyticsOpen, setAnalyticsOpen] = useState(true);

  const { data: stats } = useApi<AdminStats>("/dashboard/admin");
  const { data: leadsData } = useApi<Lead[]>("/leads");
  const { data: projectsData } = useApi<Project[]>("/projects");
  const { data: ticketsData } = useApi<Ticket[]>("/tickets");

  const leads = leadsData || [];
  const projects = projectsData || [];
  const tickets = ticketsData || [];
  const newLeads = leads.filter((l) => l.status === "new");
  const openTickets = tickets.filter((t) => t.status === "open" || t.status === "in_progress");

  const statusColors: Record<string, string> = {
    new: "text-electric-blue bg-electric-blue/10 border-electric-blue/30",
    contacted: "text-cyber-orange bg-cyber-orange/10 border-cyber-orange/30",
    proposal: "text-cyber-green bg-cyber-green/10 border-cyber-green/30",
    won: "text-cyber-green bg-cyber-green/10 border-cyber-green/30",
    lost: "text-silver-dim bg-dark-border/30 border-dark-border",
  };

  const priorityColors: Record<string, string> = {
    low: "text-silver-dim",
    medium: "text-cyber-orange",
    high: "text-cyber-red",
    urgent: "text-cyber-red",
  };

  const ticketStatusColors: Record<string, string> = {
    open: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    in_progress: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    closed: "text-silver-dim border-dark-border bg-dark-border/20",
  };

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Platform Overview
            </h1>
            <p className="text-silver text-sm mt-1">
              {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/leads"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/30 text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line text-sm" />
              New Lead
            </Link>
            <Link
              to="/admin/projects"
              className="flex items-center gap-1.5 bg-electric-blue text-deep-black font-bold px-4 py-2 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              New Project
            </Link>
          </div>
        </div>

        {/* Primary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon="ri-group-line"
            label="Total Clients"
            value={String(stats?.totalClients || 0)}
            color="text-electric-blue"
            bgColor="bg-electric-blue/10"
            borderColor="border-electric-blue/20"
            sub="Active accounts"
          />
          <StatCard
            icon="ri-folder-line"
            label="Active Projects"
            value={String(stats?.activeProjects || 0)}
            color="text-cyber-green"
            bgColor="bg-cyber-green/10"
            borderColor="border-cyber-green/20"
            sub="In progress"
          />
          <StatCard
            icon="ri-money-dollar-circle-line"
            label="Monthly Revenue"
              value={`${(stats?.mrr || 0).toLocaleString()} FCFA`}
            color="text-cyber-orange"
            bgColor="bg-cyber-orange/10"
            borderColor="border-cyber-orange/20"
            sub="MRR this month"
          />
          <StatCard
            icon="ri-user-add-line"
            label="New Leads"
            value={String(stats?.newLeads || 0)}
            color="text-cyber-red"
            bgColor="bg-cyber-red/10"
            borderColor="border-cyber-red/20"
            sub="This week"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon="ri-customer-service-line"
            label="Open Tickets"
            value={String(stats?.openTickets || 0)}
            color="text-cyber-orange"
            bgColor="bg-cyber-orange/10"
            borderColor="border-dark-border"
          />
          <StatCard
            icon="ri-bar-chart-line"
            label="Total Revenue"
              value={`${(stats?.totalRevenue || 0).toLocaleString()} FCFA`}
            color="text-cyber-green"
            bgColor="bg-cyber-green/10"
            borderColor="border-dark-border"
          />
          <StatCard
            icon="ri-time-line"
            label="Unassigned Leads"
            value={String(newLeads.length)}
            color="text-cyber-red"
            bgColor="bg-cyber-red/10"
            borderColor="border-dark-border"
          />
          <StatCard
            icon="ri-alert-line"
            label="Urgent Tickets"
            value={String(stats?.urgentTickets || 0)}
            color="text-cyber-red"
            bgColor="bg-cyber-red/10"
            borderColor="border-dark-border"
          />
        </div>

        {/* Analytics Dashboard Section */}
        <div className="border border-dark-border rounded-2xl overflow-hidden">
          <button
            onClick={() => setAnalyticsOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4 bg-dark-card hover:bg-dark-panel transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-electric-blue/10 border border-electric-blue/20 text-electric-blue">
                <i className="ri-bar-chart-2-line text-sm" />
              </div>
              <div className="text-left">
                <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Analytics Dashboard
                </h2>
                <p className="text-silver-dim text-xs">Revenue, tickets &amp; project performance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-electric-blue bg-electric-blue/10 border border-electric-blue/20 px-2.5 py-1 rounded-full font-semibold">
                Live Data
              </span>
              <i className={`text-silver transition-transform duration-200 ${analyticsOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
            </div>
          </button>

          {analyticsOpen && (
            <div className="p-5 bg-dark-panel/30 space-y-5 border-t border-dark-border">
<RevenueChart revenueByMonth={stats?.revenueByMonth || []} />
              <LeadsFunnelChart leads={leads} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <TicketResolutionChart tickets={tickets} />
                <ProjectCompletionMetrics projects={projects} />
              </div>
              <KpiCards />
            </div>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Tabbed Activity */}
          <div className="lg:col-span-2 bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="flex items-center border-b border-dark-border px-5">
              {(["leads", "projects", "tickets"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-widest border-b-2 transition-all cursor-pointer capitalize whitespace-nowrap ${
                    activeTab === tab
                      ? "border-electric-blue text-electric-blue"
                      : "border-transparent text-silver hover:text-white"
                  }`}
                >
                  {tab}
                </button>
              ))}
              <div className="ml-auto">
                <Link
                  to={`/admin/${activeTab}`}
                  className="text-electric-blue text-xs hover:underline cursor-pointer whitespace-nowrap"
                >
                  View all &rarr;
                </Link>
              </div>
            </div>

            <div className="p-5">
              {activeTab === "leads" && (
                <div className="space-y-2">
                  {leads.slice(0, 6).map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-dark-panel transition-colors cursor-pointer group">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue text-xs font-black flex-shrink-0">
                          {lead.name.split(" ").map((n: string) => n[0]).join("")}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-semibold truncate group-hover:text-electric-blue transition-colors">{lead.name}</p>
                          <p className="text-silver-dim text-xs truncate">{lead.company || "No company"} &middot; {lead.service_interest || "General"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${statusColors[lead.status]}`}>
                          {lead.status}
                        </span>
                        <i className="ri-arrow-right-s-line text-silver-dim text-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                  {leads.length === 0 && <p className="text-silver-dim text-sm">No leads yet.</p>}
                </div>
              )}

              {activeTab === "projects" && (
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div key={project.id} className="p-3 rounded-xl hover:bg-dark-panel transition-colors cursor-pointer group">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white text-sm font-semibold group-hover:text-electric-blue transition-colors">{project.title}</p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${
                            project.status === "active" ? "text-cyber-green border-cyber-green/30 bg-cyber-green/10" :
                            project.status === "paused" ? "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10" :
                            "text-electric-blue border-electric-blue/30 bg-electric-blue/10"
                          }`}>{project.status}</span>
                          <span className="text-electric-blue text-xs font-bold">{project.progress}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-dark-border rounded-full h-1.5 mb-1.5">
                        <div
                          className="h-1.5 rounded-full bg-gradient-to-r from-electric-blue to-cyber-blue transition-all"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  {projects.length === 0 && <p className="text-silver-dim text-sm">No projects yet.</p>}
                </div>
              )}

              {activeTab === "tickets" && (
                <div className="space-y-2">
                  {openTickets.slice(0, 6).map((ticket) => (
                    <div key={ticket.id} className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-dark-panel transition-colors cursor-pointer group">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          ticket.priority === "urgent" || ticket.priority === "high" ? "bg-cyber-red" :
                          ticket.priority === "medium" ? "bg-cyber-orange" : "bg-silver-dim"
                        }`} />
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate group-hover:text-electric-blue transition-colors">{ticket.subject}</p>
                          <p className="text-silver-dim text-xs"><span className={priorityColors[(ticket as any).priority] || "text-silver-dim"}>{((ticket as any).priority || "") as string}</span></p>

                        </div>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${ticketStatusColors[ticket.status]}`}>
                        {ticket.status.replace("_", " ")}
                      </span>
                    </div>
                  ))}
                  {openTickets.length === 0 && <p className="text-silver-dim text-sm">No open tickets.</p>}
                </div>
              )}
            </div>
          </div>

          {/* Right: Activity Feed */}
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
              <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Live Activity
              </h2>
              <span className="flex items-center gap-1.5 text-xs text-cyber-green">
                <span className="w-1.5 h-1.5 rounded-full bg-cyber-green animate-pulse" />
                Live
              </span>
            </div>
            <div className="p-4 space-y-3">
              {buildActivityFeed({ leads, projects, tickets }).map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full border flex-shrink-0 ${item.color}`}>
                    <i className={`${item.icon} text-xs`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-silver text-xs leading-relaxed">{item.text}</p>
                    <p className="text-silver-dim text-xs mt-1">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Email Notifications Tester */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Email Notifications
              </h2>
              <p className="text-silver-dim text-sm">Send test emails to real clients or team members</p>
            </div>
            <div className="text-xs px-3 py-1 rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue">
              Live
            </div>
          </div>

          <EmailNotificationTester />
        </div>

        {/* Bottom: Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Manage Portfolio", icon: "ri-gallery-line", href: "/admin/portfolio", color: "text-electric-blue", border: "border-electric-blue/20", bg: "bg-electric-blue/5" },
            { label: "View Leads CRM", icon: "ri-user-add-line", href: "/admin/leads", color: "text-cyber-green", border: "border-cyber-green/20", bg: "bg-cyber-green/5" },
            { label: "Generate Invoice", icon: "ri-file-list-3-line", href: "/admin/invoices", color: "text-cyber-orange", border: "border-cyber-orange/20", bg: "bg-cyber-orange/5" },
            { label: "Team Settings", icon: "ri-team-line", href: "/admin/team", color: "text-cyber-red", border: "border-cyber-red/20", bg: "bg-cyber-red/5" },
          ].map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`flex items-center gap-3 p-4 rounded-xl border ${link.border} ${link.bg} hover:border-opacity-60 transition-all cursor-pointer group`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-dark-card border border-dark-border ${link.color} flex-shrink-0`}>
                <i className={`${link.icon} text-sm`} />
              </div>
              <span className={`text-sm font-medium ${link.color} group-hover:text-white transition-colors whitespace-nowrap`}>
                {link.label}
              </span>
              <i className="ri-arrow-right-line text-silver-dim text-xs ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

